/**
 * MotivationSelector - Manual seller motivation signal input
 *
 * Allows users to manually flag seller motivation signals that
 * can't be auto-detected, such as information from calls or research.
 */

// React is auto-imported via JSX transform
import {
  AlertTriangle,
  Home,
  TrendingDown,
  Clock,
  DollarSign,
  Heart,
  Users,
  FileX,
  User,
} from 'lucide-react';

interface MotivationSelectorProps {
  propertyId: number;
  selectedMotivations: string[];
  inferredMotivations?: { signal: string; confidence: number }[];
  onMotivationsChange: (motivations: string[]) => void;
  isUpdating?: boolean;
  className?: string;
}

const MOTIVATION_OPTIONS = [
  {
    id: 'foreclosure',
    label: 'Foreclosure',
    icon: AlertTriangle,
    color: 'text-red-400',
    description: 'Property in foreclosure or pre-foreclosure',
  },
  {
    id: 'vacant',
    label: 'Vacant',
    icon: Home,
    color: 'text-cyan-400',
    description: 'Property appears vacant or unoccupied',
  },
  {
    id: 'low_equity',
    label: 'Low Equity',
    icon: TrendingDown,
    color: 'text-amber-400',
    description: 'Seller has little equity in the property',
  },
  {
    id: 'days_on_market',
    label: 'Extended DOM',
    icon: Clock,
    color: 'text-purple-400',
    description: 'Listed for extended period (60+ days)',
  },
  {
    id: 'motivated',
    label: 'Motivated Seller',
    icon: DollarSign,
    color: 'text-green-400',
    description: 'Seller expressed urgency or flexibility',
  },
  {
    id: 'divorce',
    label: 'Divorce',
    icon: Heart,
    color: 'text-pink-400',
    description: 'Selling due to divorce',
  },
  {
    id: 'inheritance',
    label: 'Inheritance',
    icon: Users,
    color: 'text-indigo-400',
    description: 'Inherited property, heirs want to sell',
  },
  {
    id: 'expired_listing',
    label: 'Expired Listing',
    icon: FileX,
    color: 'text-orange-400',
    description: 'Previously listed, listing expired',
  },
  {
    id: 'fsbo',
    label: 'FSBO',
    icon: User,
    color: 'text-blue-400',
    description: 'For Sale By Owner',
  },
];

export function MotivationSelector({
  propertyId: _propertyId,
  selectedMotivations,
  inferredMotivations = [],
  onMotivationsChange,
  isUpdating = false,
  className = '',
}: MotivationSelectorProps) {
  const handleToggle = (motivationId: string) => {
    if (isUpdating) return;

    if (selectedMotivations.includes(motivationId)) {
      onMotivationsChange(selectedMotivations.filter((m) => m !== motivationId));
    } else {
      onMotivationsChange([...selectedMotivations, motivationId]);
    }
  };

  const isInferred = (id: string) =>
    inferredMotivations.some((m) => m.signal === id);

  const getConfidence = (id: string) =>
    inferredMotivations.find((m) => m.signal === id)?.confidence;

  return (
    <div className={`bg-slate-900 rounded-xl p-4 ${className}`}>
      <h4 className="text-sm font-medium text-slate-200 mb-3">
        Seller Motivation Signals
      </h4>
      <p className="text-sm text-slate-400 mb-4">
        Check signals you've identified from calls or research. Auto-detected
        signals are marked.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {MOTIVATION_OPTIONS.map((option) => {
          const Icon = option.icon;
          const inferred = isInferred(option.id);
          const confidence = getConfidence(option.id);
          const selected = selectedMotivations.includes(option.id);

          return (
            <button
              key={option.id}
              onClick={() => handleToggle(option.id)}
              disabled={isUpdating}
              className={`
                flex items-center gap-2 p-2 rounded-lg text-left text-sm
                transition-colors border
                ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}
                ${
                  selected || inferred
                    ? 'bg-slate-800 border-slate-600'
                    : 'bg-slate-900 border-slate-700 hover:border-slate-600'
                }
              `}
              title={option.description}
            >
              <div
                className={`p-1 rounded ${
                  selected || inferred ? 'bg-slate-700' : 'bg-slate-800'
                }`}
              >
                <Icon className={`w-3 h-3 ${option.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <span
                  className={`block truncate ${
                    selected || inferred ? 'text-slate-200' : 'text-slate-400'
                  }`}
                >
                  {option.label}
                </span>
                {inferred && confidence && (
                  <span className="text-sm text-emerald-400">
                    Auto ({(confidence * 100).toFixed(0)}%)
                  </span>
                )}
              </div>
              {(selected || inferred) && (
                <div
                  className={`w-2 h-2 rounded-full ${
                    inferred ? 'bg-emerald-400' : 'bg-blue-400'
                  }`}
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex items-center gap-4 text-sm text-slate-500">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-emerald-400" />
          <span>Auto-detected</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-blue-400" />
          <span>Manual</span>
        </div>
      </div>
    </div>
  );
}
